import { useSelector } from 'react-redux';
import type { PdfPerson, PdfTradeIn } from '@makemydeal/dr-platform-types';
import type { DealXG as SharedDealXG } from '@makemydeal/dr-shared-bff-types';
import {
    mapProposalCoBuyerName,
    mapProposalSalesPerson,
    mapProposalShopper,
    mapProposalTradeIns
} from '@makemydeal/dr-sales-checkin-utils';
import { DXG_PUBLISHED_VERSION_BAGGAGE_NAME, type DealXG } from '@makemydeal/dr-sales-checkin-types';
import { ApiRequestStatus, type PublishedVersionBaggageValue } from '@makemydeal/dr-shared-types';
import { getBaggageEntryByName } from '@makemydeal/dr-platform-shared';
import type { SalesCheckinRootState } from '../common/types/SalesCheckinRootState';
import { Actions } from '../actionTypes/actionTypes';
import { DEAL_STATUS_SOLD } from './constants';

/** Status of the in-flight deal save; `Idle` before any save has run. */
export const getUpdateDealStatus = (state: SalesCheckinRootState): ApiRequestStatus =>
    state.salesCheckin?.activeDeal?.updateDealStatus ?? ApiRequestStatus.Idle;

/** True while a deal save is in flight — used to block a second submission. */
export const isDealUpdating = (state: SalesCheckinRootState): boolean => getUpdateDealStatus(state) === ApiRequestStatus.Loading;

export const getActiveDeal = (state: SalesCheckinRootState): DealXG =>
    (state.salesCheckin?.activeDeal?.details as DealXG) || ({} as DealXG);

const getCoxAutoReferences = (state: SalesCheckinRootState) => getActiveDeal(state).coxAutoReferences;

export const getActiveDealCommonOrgId = (state: SalesCheckinRootState): string =>
    (getCoxAutoReferences(state)?.commonOrgId as string) || '';

export const getDealRefId = (state: SalesCheckinRootState): string => getCoxAutoReferences(state)?.dealRefId ?? '';
export const updateDealRefId = (dealRefId: string) => ({
    type: Actions.UPDATE_DEALREFID,
    payload: { dealRefId }
});

export const getSellingVehicle = (state: SalesCheckinRootState) => getActiveDeal(state).dealStructure?.vehicles?.sellingVehicle;

/**
 * A trade-in is persisted on the deal. `dealXgMapper` filters out invalid trades before writing
 * `tradeIns`, so a non-empty list means a real trade was captured.
 */
export const hasTradeIn = (state: SalesCheckinRootState): boolean =>
    !!getActiveDeal(state).dealStructure?.vehicles?.tradeInVehicles?.tradeIns?.length;

export const getPartnerCode = (state: SalesCheckinRootState): string | undefined =>
    getCoxAutoReferences(state)?.dealRefIdPartner?.partnerCode ?? undefined;

export const getPartnerDealerId = (state: SalesCheckinRootState): string | undefined =>
    getCoxAutoReferences(state)?.dealRefIdPartner?.partnerDealerId ?? undefined;

export const getDealXgId = (state: SalesCheckinRootState): string => getActiveDeal(state).dealId ?? '';

/** True when this deal's current structured version was created by AMD/AMD Elite/a Tier-1 OEM site (F302128 Part A). */
export const getIsAmdOriginated = (state: SalesCheckinRootState): boolean =>
    !!getActiveDeal(state).dealStructure?.versionCreatedBy?.applicationName?.includes('AMD');

export const useActiveDealSelector = () => {
    const state = useSelector((state: SalesCheckinRootState) => state.salesCheckin?.activeDeal || {});
    return state;
};

export const useDealRefIdSelector = (): string => useSelector(getDealRefId);

export const usePostalCodeSelector = (): string => {
    return useSelector(
        (state: SalesCheckinRootState) => getActiveDeal(state).dealStructure?.participants?.buyer?.address?.postalCode || ''
    );
};

export const useSellingVehicleSelector = () => {
    return useSelector((state: SalesCheckinRootState) => getSellingVehicle(state) || {});
};

export const getDealXgVersion = (state: SalesCheckinRootState): string => getActiveDeal(state).dealStructure?.version ?? '';

export const getDealVisibility = (state: SalesCheckinRootState) => getActiveDeal(state).dealStructure?.visibility;

export const getAcceptedByBuyer = (state: SalesCheckinRootState): boolean | undefined =>
    getActiveDeal(state).dealStructure?.acceptedByBuyer;

// Buyer last name for the browser tab title, mirroring MV's getShopperLastName. checkedInCustomer wins
// since a customer card edit refreshes it without re-seeding the deal; deal is the fallback.
export const getBuyerLastName = (state: SalesCheckinRootState): string =>
    state.salesCheckin?.customer?.checkedInCustomer?.lastName ||
    getActiveDeal(state).dealStructure?.participants?.buyer?.lastName ||
    '';

// Mirrors MV's getIsDealSoldOrContracted; SCI has no contracting-status slice, so "contracted" keys off contractSignedDate.
export const getIsDealSoldOrContracted = (state: SalesCheckinRootState): boolean => {
    const dealLifecycle = getActiveDeal(state).dealLifecycle;
    return dealLifecycle?.status === DEAL_STATUS_SOLD || Boolean(dealLifecycle?.contractSignedDate);
};

const getPublishedVersionContent = (state: SalesCheckinRootState) =>
    getBaggageEntryByName<PublishedVersionBaggageValue>(getActiveDeal(state).baggage, DXG_PUBLISHED_VERSION_BAGGAGE_NAME)?.value
        ?.content;

export const getPublishedVersion = (state: SalesCheckinRootState): string | undefined => getPublishedVersionContent(state)?.version;

export const getPublishedBy = (state: SalesCheckinRootState): string =>
    getPublishedVersionContent(state)?.publishedBy?.trim() ?? '';

export const getPublishedVersionComment = (state: SalesCheckinRootState): string =>
    getPublishedVersionContent(state)?.versionComment?.trim() ?? '';

export const getPublishedProposalId = (state: SalesCheckinRootState): string | undefined =>
    getPublishedVersionContent(state)?.proposalId || undefined;

// Whether the deal has a financial record: either the deal360.publishedVersion baggage entry
// exists at all (MV has saved/printed the deal at some point — its `content.version` is emptied
// but the entry itself left in place once the deal is edited past a publish, see
// sentForDeskingRule, so entry presence is what matters, not `!!getPublishedVersion(...)`), or
// a manager has approved the deal (approvedByManager), which precedes the baggage entry being
// written.
export const hasFinancialRecord = (state: SalesCheckinRootState): boolean =>
    !!getBaggageEntryByName(getActiveDeal(state).baggage, DXG_PUBLISHED_VERSION_BAGGAGE_NAME) ||
    getDealVisibility(state)?.approvedByManager === true;

const getActiveDealForProposal = (state: SalesCheckinRootState): SharedDealXG => getActiveDeal(state) as unknown as SharedDealXG;

export const getSalesPerson = (state: SalesCheckinRootState): PdfPerson | undefined =>
    mapProposalSalesPerson(getActiveDealForProposal(state));

export const getBuyerInfo = (state: SalesCheckinRootState): PdfPerson => mapProposalShopper(getActiveDealForProposal(state));

export const getCoBuyerName = (state: SalesCheckinRootState): string | undefined =>
    mapProposalCoBuyerName(getActiveDealForProposal(state));

export const getTradeIns = (state: SalesCheckinRootState): PdfTradeIn[] =>
    mapProposalTradeIns(getActiveDealForProposal(state)).trades;
